import { useEffect } from 'react';
import { CompositeAdapter, InAppAdapter, SystemNotificationAdapter, type NotificationAdapter } from '../notifications/adapter';
import { displayTitle } from '../notes/model';
import { Scheduler } from '../scheduling/scheduler';
import { store } from '../state/store';
import { claimDelivery, pruneDeliveries, updateDelivery } from '../storage/repo';

class OptInSystemAdapter extends SystemNotificationAdapter {
  permission() {
    const p = super.permission();
    // Respect the in-app switch even when the browser permission is granted.
    return p === 'granted' && !store.get().settings.notificationsOptIn ? 'default' : p;
  }
}

export function createAdapter(): NotificationAdapter & { channelFor: () => 'system' | 'in-app' } {
  return new CompositeAdapter(new OptInSystemAdapter(), new InAppAdapter((p) => store.pushInbox(p)));
}

/** Starts the in-app scheduler and keeps it in step with visibility, focus and data changes. */
export function useScheduler(ready: boolean) {
  useEffect(() => {
    if (!ready) return;
    const scheduler = new Scheduler({
      now: () => Date.now(),
      getSchedules: () => store.get().schedules,
      getSettings: () => store.get().settings,
      noteTitle: (id) => {
        const n = store.get().notes[id];
        return n ? displayTitle(n) : '';
      },
      getLastCheck: () => store.get().meta.lastSchedulerCheck,
      setLastCheck: (t) => store.setLastSchedulerCheck(t),
      claim: claimDelivery,
      adapter: createAdapter(),
      onMissed: (r) => {
        const n = store.get().notes[r.schedule.noteId];
        store.pushInbox(
          { key: r.key, scheduleId: r.schedule.id, noteId: r.schedule.noteId, title: r.schedule.title || (n ? displayTitle(n) : 'Reminder'), body: 'Missed while MemoNest was closed', occurrence: r.occurrence },
          true,
        );
      },
      onTick: () => store.refreshActive(),
    });
    scheduler.start();
    const kick = () => {
      if (document.visibilityState === 'visible') void scheduler.check();
    };
    document.addEventListener('visibilitychange', kick);
    window.addEventListener('focus', kick);
    window.addEventListener('online', kick);
    let lastSchedules = store.get().schedules;
    const unsub = store.subscribe(() => {
      const s = store.get().schedules;
      if (s !== lastSchedules) {
        lastSchedules = s;
        void scheduler.check();
      }
    });
    void pruneDeliveries(Date.now() - 90 * 864e5).catch(() => undefined);
    return () => {
      scheduler.stop();
      unsub();
      document.removeEventListener('visibilitychange', kick);
      window.removeEventListener('focus', kick);
      window.removeEventListener('online', kick);
    };
  }, [ready]);
}

export async function handleReminderAction(action: 'snooze' | 'done' | 'dismiss' | 'open', key: string, scheduleId: string): Promise<void> {
  const s = store.get().schedules.find((x) => x.id === scheduleId);
  store.dismissInbox(key);
  if (action === 'snooze' && s) {
    await store.saveSchedule({ ...s, snoozedUntil: Date.now() + 10 * 60_000, updatedAt: Date.now() });
    await updateDelivery(key, { status: 'snoozed' });
    store.toast('Snoozed for 10 minutes.');
  } else if (action === 'done') {
    await updateDelivery(key, { status: 'completed' });
  } else if (action === 'dismiss') {
    await updateDelivery(key, { status: 'dismissed' });
  }
}
